/**
 * dialog-hero.tsx
 * Shared dialog header used across the admin's form/editor dialogs.
 * Renders the reference "icon-tile" header — a navy (or
 * destructive-tinted) rounded icon square next to a title + optional subtitle —
 * so every dialog opens with the same chrome instead of each one hand-rolling
 * its own heading.
 *
 * It renders a real `DialogHeader` so the drag-to-move behaviour wired through
 * `DialogDragContext` keeps working, and a `DialogTitle` so Radix's
 * accessibility contract is satisfied. Drop it as the first child of
 * `DialogContent`.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

interface DialogHeroProps {
  /** Lucide icon shown inside the tile (sized 16px). */
  icon: LucideIcon;
  /** Dialog title — the primary heading. */
  title: ReactNode;
  /** Optional one-line clarifier under the title, in muted text. */
  subtitle?: ReactNode;
  /** Tile colour: navy accent (default) or destructive-tinted for deletes. */
  tone?: 'navy' | 'destructive';
  /** Extra classes forwarded to the underlying `DialogHeader`. */
  className?: string;
}

/**
 * Icon-tile dialog header. Pure presentation; owns no spacing above/below
 * beyond the `DialogHeader` defaults — callers control padding via the
 * surrounding `DialogContent`.
 */
export function DialogHero({ icon: Icon, title, subtitle, tone = 'navy', className }: DialogHeroProps) {
  return (
    <DialogHeader className={cn('flex-row items-center gap-3 space-y-0 text-left', className)}>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
          tone === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 space-y-0.5">
        <DialogTitle className="text-base font-semibold leading-tight">{title}</DialogTitle>
        {subtitle && (
          <DialogDescription className="text-xs text-muted-foreground">{subtitle}</DialogDescription>
        )}
      </div>
    </DialogHeader>
  );
}
